// src/pages/NotificationsPage/NotificationsList.jsx
import React from 'react';
import { notificationsStyles } from './styles';
import { groupNotifications } from './notificationUtils';
import NotificationGroup from './NotificationGroup';
import { COLORS } from '../../constants';

// Empty Bell Icon SVG
const EmptyBellIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="1.5" opacity="0.3">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <circle cx="18" cy="6" r="3" fill={COLORS.primary} />
  </svg>
);

// Loading Spinner SVG
const LoadingSpinner = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={COLORS.primary} strokeWidth="3" strokeLinecap="round" strokeDasharray="32 32" opacity="0.25" />
    <circle cx="12" cy="12" r="10" stroke={COLORS.primary} strokeWidth="3" strokeLinecap="round" strokeDasharray="32 32" strokeDashoffset="8">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const NotificationsList = ({
  notifications,
  setNotifications,
  loading,
  senders,
  expanded,
  setExpanded,
  user,
  respondingId,
  setRespondingId,
  response,
  setResponse,
  justSuggestedId,
  setJustSuggestedId
}) => {
  // Show loading state
  if (loading) {
    return (
      <div style={notificationsStyles.loadingState}>
        <LoadingSpinner />
        <h3 style={notificationsStyles.loadingTitle}>Loading notifications...</h3>
        <p style={notificationsStyles.loadingMessage}>
          Please wait while we fetch your notifications
        </p>
      </div>
    );
  }

  // Show empty state
  if (notifications.length === 0) {
    return (
      <div style={notificationsStyles.emptyState}>
        <EmptyBellIcon />
        <h3 style={notificationsStyles.emptyTitle}>No Notifications</h3>
        <p style={notificationsStyles.emptyMessage}>
          You're all caught up! We'll notify you when something happens.
        </p>
      </div>
    );
  }

  // Separate meeting invites from other notifications
  const meetingNotifications = notifications.filter(n => n.type === 'invite' || n.type === 'invite-response');
  const otherNotifications = notifications.filter(n => n.type !== 'invite' && n.type !== 'invite-response');

  // Group meeting notifications by conversation chain
  const grouped = groupNotifications(meetingNotifications);

  const toggleExpanded = (groupKey) => {
    setExpanded(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Sort groups by most recent notification (newest conversations first)
  const sortedGroups = Object.entries(grouped).sort(([, notesA], [, notesB]) => {
    const latestA = notesA[notesA.length - 1];
    const latestB = notesB[notesB.length - 1];
    const timeA = latestA.createdAt?.toMillis ? latestA.createdAt.toMillis() : 0;
    const timeB = latestB.createdAt?.toMillis ? latestB.createdAt.toMillis() : 0;
    return timeB - timeA; // Newest first
  });

  // Sort other notifications by date (newest first)
  const sortedOtherNotifications = otherNotifications.sort((a, b) => {
    const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return timeB - timeA;
  });

  // Combine and sort ALL notifications by timestamp for proper chronological order
  const allItems = [];
  
  // Add individual notifications with their timestamps
  sortedOtherNotifications.forEach(notification => {
    allItems.push({
      type: 'individual',
      notification,
      timestamp: notification.createdAt?.toMillis() || 0
    });
  });
  
  // Add grouped notifications with their latest timestamp
  sortedGroups.forEach(([groupKey, notes]) => {
    const latestNote = notes[notes.length - 1];
    allItems.push({
      type: 'group',
      groupKey,
      notes,
      timestamp: latestNote.createdAt?.toMillis() || 0
    });
  });
  
  // Sort all items by timestamp (newest first)
  const sortedAllItems = allItems.sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div style={notificationsStyles.notificationsList}>
      {/* All notifications sorted chronologically (newest first) */}
      {sortedAllItems.map((item, index) => {
        if (item.type === 'individual') {
          const notification = item.notification;
          // Determine notification type and colors
          const isSale = notification.type === 'sale_completed' || notification.title?.includes('Sold');
          
          return (
            <div key={notification.id} style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e8e8e8',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '16px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: isSale ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  color: isSale ? '#15803d' : '#1d4ed8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {isSale ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9M12,4.5A7.5,7.5 0 0,0 4.5,12A7.5,7.5 0 0,0 12,19.5A7.5,7.5 0 0,0 19.5,12A7.5,7.5 0 0,0 12,4.5Z"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17,18C17.5,18 18,17.5 18,17C18,16.5 17.5,16 17,16C16.5,16 16,16.5 16,17C16,17.5 16.5,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5H5.21L4.27,3H1M7,18C7.5,18 8,17.5 8,17C8,16.5 7.5,16 7,16C6.5,16 6,16.5 6,17C6,17.5 6.5,18 7,18Z"/>
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: '0 0 8px 0'
                  }}>
                    {notification.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#6b7280',
                    margin: '0 0 12px 0',
                    lineHeight: '1.5'
                  }}>
                    {notification.message}
                  </p>
                  <div style={{
                    fontSize: '13px',
                    color: '#9ca3af'
                  }}>
                    {notification.createdAt && notification.createdAt.toDate ? 
                      notification.createdAt.toDate().toLocaleString() : 
                      'Recently'
                    }
                  </div>
                </div>
              </div>
            </div>
          );
        } else if (item.type === 'group') {
          const isExpanded = expanded[item.groupKey];
          // Reverse notes to show newest first within each group
          const reversedNotes = [...item.notes].reverse();

          return (
            <NotificationGroup
              key={item.groupKey}
              groupKey={item.groupKey}
              notes={reversedNotes}
              isExpanded={isExpanded}
              onToggleExpanded={toggleExpanded}
              senders={senders}
              user={user}
              respondingId={respondingId}
              setRespondingId={setRespondingId}
              response={response}
              setResponse={setResponse}
              justSuggestedId={justSuggestedId}
              setNotifications={setNotifications}
              setJustSuggestedId={setJustSuggestedId}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default NotificationsList;